import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function Upgrade() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [excludedStudents, setExcludedStudents] = useState([]);
  const [excludeFailed, setExcludeFailed] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [nextClass, setNextClass] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Helper function to get base class name (without section)
  const getClassDisplayName = (className) => {
    if (!className) return '';
    // Remove section part (e.g., "Grade 1 - Section A" -> "Grade 1")
    return className.split(' - ')[0].trim();
  };

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchClasses();
      fetchGrades();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections();
    } else {
      setSections([]);
      setSelectedSection('');
      setStudents([]);
      setExcludedStudents([]);
      setNextClass(null);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection) {
      fetchStudents();
    } else if (selectedClass && !selectedSection) {
      setStudents([]);
      setExcludedStudents([]);
      setNextClass(null);
    }
  }, [selectedClass, selectedSection, excludeFailed]);

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('id, class_name, grade_id, stream_id, branch_id')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      // Filter by branch if one is selected
      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }
      // If no branch is selected, show all classes (no branch filter)

      const { data, error } = await query.order('class_name', { ascending: true });

      if (error) throw error;
      
      // Deduplicate classes by base class name (without section)
      const uniqueClassesMap = new Map();
      (data || []).forEach(cls => {
        const baseName = getClassDisplayName(cls.class_name);
        if (!uniqueClassesMap.has(baseName)) {
          uniqueClassesMap.set(baseName, cls);
        }
      });
      
      setClasses(Array.from(uniqueClassesMap.values()));
    } catch (error) {
      console.error('Error fetching classes:', error);
      setMessage({ type: 'error', text: 'Failed to load classes' });
    }
  };

  const fetchSections = async () => {
    if (!selectedClass || !schoolData?.id) {
      setSections([]);
      return;
    }

    try {
      const classId = parseInt(selectedClass);
      
      // Get the selected class from database to find its base class name
      let selectedClassQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('id', classId)
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        selectedClassQuery = selectedClassQuery.eq('branch_id', selectedBranchId);
      }

      const { data: selectedClassData, error: selectedClassError } = await selectedClassQuery.maybeSingle();

      if (selectedClassError || !selectedClassData) {
        console.error('Error fetching selected class:', selectedClassError);
        setSections([]);
        return;
      }

      const baseClassName = getClassDisplayName(selectedClassData.class_name);

      // Find all classes with the same base class name to get all sections
      let query = supabase
        .from('classes')
        .select('id, class_name, sections')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data: allClassesData, error: classesError } = await query;

      if (classesError) {
        console.error('Error fetching classes for sections:', classesError);
        setSections([]);
        return;
      }

      // Filter classes where the base class name matches
      const matchingClasses = (allClassesData || []).filter(cls => {
        const clsBaseName = getClassDisplayName(cls.class_name);
        return clsBaseName === baseClassName;
      });

      // Collect all sections from all matching classes
      const allSections = new Set();
      matchingClasses.forEach(cls => {
        const classSections = cls.sections || [];
        if (Array.isArray(classSections)) {
          classSections.forEach(section => {
            if (section && (typeof section === 'string' ? section.trim() !== '' : true)) {
              allSections.add(typeof section === 'string' ? section.trim() : String(section));
            }
          });
        } else if (typeof classSections === 'string' && classSections.trim() !== '') {
          try {
            const parsed = JSON.parse(classSections);
            if (Array.isArray(parsed)) {
              parsed.forEach(section => {
                if (section && (typeof section === 'string' ? section.trim() !== '' : true)) {
                  allSections.add(typeof section === 'string' ? section.trim() : String(section));
                }
              });
            }
          } catch (e) {
            // If not JSON, treat as single section
            allSections.add(classSections.trim());
          }
        }
      });

      const sectionObjects = Array.from(allSections).sort().map((sectionName, index) => ({
        id: index + 1,
        section_name: sectionName,
        class_id: classId
      }));

      setSections(sectionObjects);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
    }
  };

  const fetchGrades = async () => {
    try {
      let query = supabase
        .from('grades')
        .select('id, name')
        .eq('school_id', schoolData.id);

      // Filter by branch if one is selected
      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }
      // If no branch is selected, show all grades (no branch filter)

      const { data, error } = await query.order('name', { ascending: true });

      if (error) throw error;
      setGrades(data || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };

  const fetchStudents = async () => {
    if (!selectedClass || !selectedSection || !schoolData?.id) return;

    try {
      setLoadingStudents(true);
      const classId = parseInt(selectedClass);

      // Get all class IDs with the same base class name
      const selectedClassData = classes.find(c => c.id === classId);
      if (!selectedClassData) {
        setStudents([]);
        return;
      }

      const baseClassName = getClassDisplayName(selectedClassData.class_name);
      
      // Find all classes with the same base class name
      let classesQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        classesQuery = classesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: allClassesData, error: classesError } = await classesQuery;
      
      if (classesError) {
        throw classesError;
      }

      const matchingClassIds = (allClassesData || [])
        .filter(cls => getClassDisplayName(cls.class_name) === baseClassName)
        .map(cls => cls.id);

      if (matchingClassIds.length === 0) {
        setStudents([]);
        return;
      }

      // Fetch students in the selected class(es) - filter by section in JavaScript
      let query = supabase
        .from('students')
        .select('id, first_name, last_name, student_id_no, class_id, section_id')
        .in('class_id', matchingClassIds)
        .eq('is_active', true)
        .order('first_name', { ascending: true });

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data: studentsData, error: studentsError } = await query;

      if (studentsError) {
        console.error('Error fetching students:', studentsError);
        throw studentsError;
      }

      // Filter students by section in JavaScript (handles case-insensitive matching)
      const filteredStudents = (studentsData || []).filter(student => {
        if (!student.section_id) return false;
        // Case-insensitive comparison
        return String(student.section_id).trim().toLowerCase() === String(selectedSection).trim().toLowerCase();
      });

      // If excludeFailed is true, fetch exam marks to identify failed students
      if (excludeFailed && filteredStudents && filteredStudents.length > 0) {
        const studentIds = studentsData.map(s => s.id);
        
        // Fetch exam marks for these students
        const { data: examMarks, error: marksError } = await supabase
          .from('exam_marks')
          .select('student_id, grade, percentage, obtained_marks, total_marks')
          .in('student_id', studentIds)
          .eq('is_active', true);

        if (!marksError && examMarks) {
          // Group marks by student
          const studentMarks = {};
          examMarks.forEach(mark => {
            if (!studentMarks[mark.student_id]) {
              studentMarks[mark.student_id] = [];
            }
            studentMarks[mark.student_id].push(mark);
          });

          // Identify failed students (grade 'F' or percentage < 50)
          const failedStudentIds = new Set();
          Object.keys(studentMarks).forEach(studentId => {
            const marks = studentMarks[studentId];
            const hasF = marks.some(m => m.grade && m.grade.toUpperCase() === 'F');
            const lowPercentage = marks.some(m => {
              if (m.percentage !== null && m.percentage < 50) return true;
              if (m.obtained_marks && m.total_marks) {
                const pct = (parseFloat(m.obtained_marks) / parseFloat(m.total_marks)) * 100;
                return pct < 50;
              }
              return false;
            });
            if (hasF || lowPercentage) {
              failedStudentIds.add(parseInt(studentId));
            }
          });

          // Auto-exclude failed students
          setExcludedStudents(Array.from(failedStudentIds));
        }
      }

      setStudents(filteredStudents || []);

      // Find next class
      const currentClass = classes.find(c => c.id === classId);
      if (currentClass && currentClass.grade_id) {
        const currentGrade = grades.find(g => g.id === currentClass.grade_id);
        if (currentGrade) {
          // Extract grade number (e.g., "Grade 1" -> 1, "Grade 10" -> 10)
          const gradeMatch = currentGrade.name.match(/\d+/);
          if (gradeMatch) {
            const currentGradeNum = parseInt(gradeMatch[0]);
            const nextGradeNum = currentGradeNum + 1;
            
            // Find next grade
            const nextGrade = grades.find(g => {
              const match = g.name.match(/\d+/);
              return match && parseInt(match[0]) === nextGradeNum;
            });

            if (nextGrade) {
              // Find or create next class with same stream
              let nextClassData = classes.find(c => 
                c.grade_id === nextGrade.id && 
                c.stream_id === currentClass.stream_id
              );

              if (!nextClassData) {
                // Try to find any class with the next grade
                nextClassData = classes.find(c => c.grade_id === nextGrade.id);
              }

              setNextClass(nextClassData);
            } else {
              setNextClass(null);
            }
          } else {
            setNextClass(null);
          }
        } else {
          setNextClass(null);
        }
      } else {
        setNextClass(null);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setMessage({ type: 'error', text: 'Failed to load students' });
    } finally {
      setLoadingStudents(false);
    }
  };

  const toggleStudentExclusion = (studentId) => {
    setExcludedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleUpgrade = async () => {
    if (!selectedClass) {
      setMessage({ type: 'error', text: 'Please select a class first' });
      return;
    }

    if (!nextClass) {
      setMessage({ type: 'error', text: 'Next class not found. Please ensure the next grade and class exist.' });
      return;
    }

    const studentsToUpgrade = students.filter(s => !excludedStudents.includes(s.id));

    if (studentsToUpgrade.length === 0) {
      setMessage({ type: 'error', text: 'No students selected for upgrade' });
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmUpgrade = async () => {
    setShowConfirmModal(false);

    try {
      setUpgrading(true);
      setMessage({ type: '', text: '' });

      const studentIds = studentsToUpgrade.map(s => s.id);

      // Update students' class_id to next class
      const { error } = await supabase
        .from('students')
        .update({ 
          class_id: nextClass.id,
          updated_at: new Date().toISOString()
        })
        .in('id', studentIds);

      if (error) throw error;

      setMessage({ 
        type: 'success', 
        text: `Successfully upgraded ${studentsToUpgrade.length} student(s) to ${nextClass.class_name}!` 
      });

      // Refresh students list
      await fetchStudents();
    } catch (error) {
      console.error('Error upgrading students:', error);
      setMessage({ type: 'error', text: 'Failed to upgrade students. Please try again.' });
    } finally {
      setUpgrading(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
  const studentsToUpgrade = students.filter(s => !excludedStudents.includes(s.id));
  const excludedCount = excludedStudents.length;

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content" style={{ padding: '2rem', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
        <div className="content-header" style={{ marginBottom: '2rem' }}>
          <h1>Upgrade Students</h1>
          <p>Select a class and upgrade students to the next class</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`} style={{ marginBottom: '1.5rem' }}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        {/* Step 1: Select Class */}
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          padding: '1.5rem', 
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', color: '#1F2937' }}>
            Step 1: Select Class
          </h2>
          <div className="form-group">
            <label htmlFor="class-select">Select Class *</label>
            <select
              id="class-select"
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedSection('');
                setExcludedStudents([]);
              }}
              style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
            >
              <option value="">-- Select a Class --</option>
              {classes.map(cls => {
                const grade = grades.find(g => g.id === cls.grade_id);
                const baseClassName = getClassDisplayName(cls.class_name);
                return (
                  <option key={cls.id} value={cls.id}>
                    {baseClassName} {grade ? `(${grade.name})` : ''}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Step 1.5: Select Section */}
        {selectedClass && (
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            padding: '1.5rem', 
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', color: '#1F2937' }}>
              Step 1.5: Select Section
            </h2>
            <div className="form-group">
              <label htmlFor="section-select">Select Section *</label>
              <select
                id="section-select"
                value={selectedSection}
                onChange={(e) => {
                  setSelectedSection(e.target.value);
                  setExcludedStudents([]);
                }}
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
              >
                <option value="">-- Select a Section --</option>
                {sections.map(section => (
                  <option key={section.id} value={section.section_name}>
                    {section.section_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Review Students */}
        {selectedClass && selectedSection && (
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            padding: '1.5rem', 
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', color: '#1F2937' }}>
              Step 2: Review Students
            </h2>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={excludeFailed}
                  onChange={(e) => setExcludeFailed(e.target.checked)}
                />
                <span>Automatically exclude failed students (Grade F or percentage &lt; 50%)</span>
              </label>
            </div>

            {nextClass && (
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#EFF6FF', 
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <strong>Next Class:</strong> {nextClass.class_name}
              </div>
            )}

            {loadingStudents ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="loading-spinner"></div>
                <p>Loading students...</p>
              </div>
            ) : students.length === 0 ? (
              <p style={{ color: '#6B7280', textAlign: 'center', padding: '2rem' }}>
                No students found in this class.
              </p>
            ) : (
              <>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '6px'
                }}>
                  <div>
                    <strong>Total Students:</strong> {students.length}
                  </div>
                  <div>
                    <strong>To Upgrade:</strong> {studentsToUpgrade.length}
                  </div>
                  {excludedCount > 0 && (
                    <div style={{ color: '#EF4444' }}>
                      <strong>Excluded:</strong> {excludedCount}
                    </div>
                  )}
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F3F4F6', borderBottom: '2px solid #E5E7EB' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Include</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Student ID</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map(student => {
                        const isExcluded = excludedStudents.includes(student.id);
                        return (
                          <tr 
                            key={student.id}
                            style={{ 
                              borderBottom: '1px solid #E5E7EB',
                              backgroundColor: isExcluded ? '#FEE2E2' : '#FFFFFF'
                            }}
                          >
                            <td style={{ padding: '0.75rem' }}>
                              <input
                                type="checkbox"
                                checked={!isExcluded}
                                onChange={() => toggleStudentExclusion(student.id)}
                              />
                            </td>
                            <td style={{ padding: '0.75rem' }}>{student.student_id_no || 'N/A'}</td>
                            <td style={{ padding: '0.75rem' }}>
                              {student.first_name} {student.last_name}
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                              {isExcluded ? (
                                <span style={{ color: '#EF4444', fontWeight: '600' }}>Excluded</span>
                              ) : (
                                <span style={{ color: '#10B981', fontWeight: '600' }}>Included</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: Upgrade */}
        {selectedClass && selectedSection && nextClass && studentsToUpgrade.length > 0 && (
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            padding: '1.5rem', 
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', color: '#1F2937' }}>
              Step 3: Confirm Upgrade
            </h2>
            <p style={{ marginBottom: '1rem', color: '#6B7280' }}>
              {studentsToUpgrade.length} student(s) will be upgraded from{' '}
              <strong>{selectedClassData?.class_name}</strong> to{' '}
              <strong>{nextClass.class_name}</strong>.
            </p>
            {excludedCount > 0 && (
              <p style={{ marginBottom: '1rem', color: '#EF4444' }}>
                {excludedCount} student(s) will remain in the current class.
              </p>
            )}
            <button
              onClick={handleUpgrade}
              disabled={upgrading || showConfirmModal}
              className="btn-primary"
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: (upgrading || showConfirmModal) ? 'not-allowed' : 'pointer',
                opacity: (upgrading || showConfirmModal) ? 0.6 : 1
              }}
            >
              {upgrading ? 'Upgrading...' : `Upgrade ${studentsToUpgrade.length} Student(s)`}
            </button>
          </div>
        )}

        {selectedClass && selectedSection && !nextClass && students.length > 0 && (
          <div style={{ 
            backgroundColor: '#FEF3C7', 
            padding: '1rem', 
            borderRadius: '8px',
            border: '1px solid #FCD34D',
            color: '#92400E'
          }}>
            <strong>⚠️ Next class not found.</strong> Please ensure the next grade and class exist in the system.
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div 
            className="modal-backdrop" 
            onClick={() => setShowConfirmModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div 
              className="modal-card" 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
              }}
            >
              <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', color: '#1F2937' }}>
                Confirm Upgrade
              </h3>
              <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                Are you sure you want to upgrade {studentsToUpgrade.length} student(s) from{' '}
                <strong>{selectedClassData?.class_name}</strong> to{' '}
                <strong>{nextClass?.class_name}</strong>?
              </p>
              {excludedCount > 0 && (
                <p style={{ marginBottom: '1.5rem', color: '#EF4444' }}>
                  {excludedCount} student(s) will remain in the current class.
                </p>
              )}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="btn-secondary"
                  style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmUpgrade}
                  disabled={upgrading}
                  className="btn-primary"
                  style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: upgrading ? 'not-allowed' : 'pointer',
                    opacity: upgrading ? 0.6 : 1
                  }}
                >
                  {upgrading ? 'Upgrading...' : 'Confirm Upgrade'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Upgrade;
